import React, { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Users, 
  Mail, 
  Bell, 
  BarChart3,
  Send,
  RefreshCw,
  FileText
} from 'lucide-react';

export function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [messageForm, setMessageForm] = useState({
    type: 'inbox',
    subject: '',
    content: '',
    content_type: 'text',
    source: 'admin',
    recipients: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageForm.subject || !messageForm.content || messageForm.recipients.length === 0) {
      toast.error('Please fill in all required fields and select at least one recipient');
      return;
    }

    setSending(true);
    try {
      await adminAPI.createMessage(messageForm);
      toast.success('Message sent successfully');
      setMessageForm({
        type: 'inbox',
        subject: '',
        content: '',
        content_type: 'text',
        source: 'admin',
        recipients: []
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const toggleRecipient = (userId) => {
    setMessageForm(prev => ({
      ...prev,
      recipients: prev.recipients.includes(userId)
        ? prev.recipients.filter(id => id !== userId)
        : [...prev.recipients, userId]
    }));
  };

  const selectAllRecipients = () => {
    setMessageForm(prev => ({
      ...prev,
      recipients: users.map(u => u.id)
    }));
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6" data-testid="admin-loading">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage messages and view statistics
          </p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-admin-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card data-testid="stat-total-users">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Users</p>
                <p className="text-3xl font-heading font-bold mt-2">{stats?.total_users || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-total-messages">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Messages</p>
                <p className="text-3xl font-heading font-bold mt-2">{stats?.total_messages || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                <Mail className="w-6 h-6 text-secondary-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-inbox-messages">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Inbox Messages</p>
                <p className="text-3xl font-heading font-bold mt-2">{stats?.total_inbox || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-alert-messages">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Alert Messages</p>
                <p className="text-3xl font-heading font-bold mt-2">{stats?.total_alerts || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                <Bell className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="compose" className="space-y-6">
        <TabsList>
          <TabsTrigger value="compose" data-testid="compose-tab">
            <Send className="w-4 h-4 mr-2" />
            Compose Message
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab">
            <Users className="w-4 h-4 mr-2" />
            User Statistics
          </TabsTrigger>
          <TabsTrigger value="subjects" data-testid="subjects-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Subject Statistics
          </TabsTrigger>
        </TabsList>

        {/* Compose Message Tab */}
        <TabsContent value="compose">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Create New Message</CardTitle>
              <CardDescription>
                Send a message or alert to selected users
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendMessage} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="type">Message Type</Label>
                    <Select
                      value={messageForm.type}
                      onValueChange={(value) => setMessageForm({ ...messageForm, type: value })}
                    >
                      <SelectTrigger data-testid="message-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inbox">Inbox Message</SelectItem>
                        <SelectItem value="alert">Alert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="content_type">Content Type</Label>
                    <Select
                      value={messageForm.content_type}
                      onValueChange={(value) => setMessageForm({ ...messageForm, content_type: value })}
                    >
                      <SelectTrigger data-testid="content-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Plain Text</SelectItem>
                        <SelectItem value="html">HTML</SelectItem>
                        <SelectItem value="markdown">Markdown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={messageForm.subject}
                    onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                    placeholder="Enter message subject"
                    data-testid="message-subject-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Input
                    id="source"
                    value={messageForm.source}
                    onChange={(e) => setMessageForm({ ...messageForm, source: e.target.value })}
                    placeholder="e.g., admin, system, security"
                    data-testid="message-source-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Content *</Label>
                  <Textarea
                    id="content"
                    value={messageForm.content}
                    onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                    placeholder={
                      messageForm.content_type === 'markdown' 
                        ? "# Heading\n\nYour markdown content here..."
                        : messageForm.content_type === 'html'
                        ? "<h2>Heading</h2>\n<p>Your HTML content here...</p>"
                        : "Your message content here..."
                    }
                    rows={8}
                    className="font-mono text-sm"
                    data-testid="message-content-input"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Recipients *</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={selectAllRecipients}>
                      Select All
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto p-1">
                    {users.map((user) => (
                      <div 
                        key={user.id}
                        className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                          messageForm.recipients.includes(user.id) 
                            ? 'bg-primary/10 border-primary' 
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => toggleRecipient(user.id)}
                        data-testid={`recipient-${user.id}`}
                      >
                        <Checkbox 
                          checked={messageForm.recipients.includes(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{user.name}</p>
                          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                        </div>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className="text-xs">
                          {user.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {messageForm.recipients.length} recipient(s) selected
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full" 
                  disabled={sending}
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Statistics Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">User Message Statistics</CardTitle>
              <CardDescription>
                Message counts per user (content not visible)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium">User</th>
                      <th className="text-center p-3 font-medium">Unread</th>
                      <th className="text-center p-3 font-medium">Read</th>
                      <th className="text-center p-3 font-medium">Archived</th>
                      <th className="text-center p-3 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.user_stats?.map((user) => (
                      <tr key={user.user_id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </td>
                        <td className="text-center p-3">
                          <Badge variant="secondary" className="bg-primary/10 text-primary">{user.unread}</Badge>
                        </td>
                        <td className="text-center p-3">
                          <Badge variant="secondary" className="bg-green-500/10 text-green-600">{user.read}</Badge>
                        </td>
                        <td className="text-center p-3">
                          <Badge variant="outline">{user.archived}</Badge>
                        </td>
                        <td className="text-center p-3">
                          <span className="font-semibold">{user.total}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subject Statistics Tab */}
        <TabsContent value="subjects">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Subject Statistics</CardTitle>
              <CardDescription>
                Message distribution by subject (content not visible)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.subject_stats?.map((subject, index) => (
                  <div key={index} className="flex items-center justify-between p-4 rounded-md bg-muted/50">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{subject.subject}</p>
                        <Badge variant={subject.type === 'alert' ? 'default' : 'secondary'} className={`text-xs mt-1 ${
                          subject.type === 'alert' ? 'bg-accent' : ''
                        }`}>
                          {subject.type}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{subject.count}</p>
                      <p className="text-sm text-muted-foreground">messages</p>
                    </div>
                  </div>
                ))}
                {(!stats?.subject_stats || stats.subject_stats.length === 0) && (
                  <p className="text-center py-8 text-muted-foreground">No subject data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
